/**
 ShapeDiver 3D Viewer Application

 GLBLoader.js

 Content:
   Loader used to load glb files and convert them into THREE.Object3D objects.

  Copyright (c) 2018 ShapeDiver GmbH

  @module GLBLoader
 */

// require axios for AJAX requests
const AXIOS = require('../../externals/axios'),
      THREE = require('../../externals/three'),
      TO_TINY_COLOR = require('../util/toTinyColor'),
      GLOBAL_UTILS = require('../util/GlobalUtils'),
      MATERIAL_PROPERTIES_STANDARD = require('../materials/MaterialPropertiesStandard');

// FIXME optionally avoid using verb, because it can not be used with react-native (expo)
var verb, verb_util;
try {
  verb = require('../../externals/verb-nurbs');
  if (verb !== null) {
    verb_util = require('../util/verbToThreeConversion');
  }
} catch (e) {
  verb = null;
}

/**
 * Constructor of the singleton GLBLoader class
 */
var GLBLoader = new function () {
  var that = this;

  // dummy log functionality, will be replaced by the ViewerApp
  this.log = function () {};
  this.warn = function () {};
  this.error = function () {};

  /**
	 * Loads a binary glb file and creates a THREE.Object3D from the content.
	 * @param  {String} url        URL of a binary glb file
	 * @param  {THREE.Matrix4[]} transforms Array of transformation matrices to be applied to the resulting glb
	 * @param  {Number} tol        Tolerance used for discretisation of NURBS geometry. Defaults to 0.001.
	 * @return {Promise<module:OutputVersion~GeometryData[]>} Definition of geometry representing the content of the glb.
	 */
  this.loadGeometry = function (url, transforms, tol) {
    tol = tol || 0.001;
    return AXIOS({
      method: 'GET',
      url: url,
      timeout: 20000,
      crossDomain: true,
      headers: {
        'Access-Control-Allow-Origin': '*'
      },
      responseType: 'arraybuffer'
    })
      .catch(function (error) {
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          return Promise.reject(
            new Error(
              'Loading glb failed with status ' + error.response.status + '.'
            )
          );
        } else if (error.request) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          return Promise.reject(
            new Error('No response to glb download AJAX request.')
          );
        } else {
          // Something happened in setting up the request that triggered an Error
          return Promise.reject(
            new Error('Error setting up the glb download AJAX request.')
          );
        }
      })
      .then(function (response) {
        return _processBinary(response.data, transforms, tol);
      });
  };

  var _processBinary = function (binaryGeometry, transforms, tol) {
    return new Promise(function (resolve, reject) {

      let dataView = new DataView(binaryGeometry);

      // process glTF part first

      let glTFmagic = '';
      for (let i = 0; i < 4; ++i) {
        glTFmagic = glTFmagic + String.fromCharCode(dataView.getUint8(i));
      }
      if (glTFmagic != 'glTF') {
        reject(
          new Error(
            'ShapeDiverGLBLoader got invalid data: glTF magic wrong.'
          )
        );
        return;
      }

      // specification of binary gltf 1.0: https://github.com/KhronosGroup/glTF/tree/master/extensions/1.0/Khronos/KHR_binary_glTF

      //let glTFversion = dataView.getUint32(4, true);
      let binaryglTFTotalLength = dataView.getUint32(8, true);
      let sceneByteLength = dataView.getUint32(12, true);
      //let sceneFormat = dataView.getUint32(16, true);

      let glTFString = '';
      for (let i = 0; i < sceneByteLength; ++i) {
        glTFString =
					glTFString + String.fromCharCode(dataView.getUint8(20 + i));
      }
      let glTFObject = JSON.parse(glTFString);
      let glTFBinaryDataByteOffset = 20 + sceneByteLength;

      // check if all the important properties are there
      if (!glTFObject.hasOwnProperty('meshes') ||
				!glTFObject.hasOwnProperty('bufferViews') ||
				!glTFObject.hasOwnProperty('accessors')
      ) {
        reject(
          new Error(
            'Invalid JSON format in glTF file. Main object is missing required property.'
          )
        );
        return;
      }

      // read all the accessor definitions and prepare typed arrays
      let accessors = {};
      let accessorNames = Object.keys(glTFObject.accessors);

      for (let accessorNameIndex in accessorNames) {
        let accessor = glTFObject.accessors[accessorNames[accessorNameIndex]];

        let bufferViewName = accessor.bufferView;
        if (glTFObject.bufferViews.hasOwnProperty(bufferViewName)) {
          let bufferView = glTFObject.bufferViews[bufferViewName];

          // create accessor object
          let accessorObject = {};

          // check if we read a vector or a scalar
          if (!accessor.hasOwnProperty('type') ||
						!accessor.hasOwnProperty('count') ||
						!accessor.hasOwnProperty('componentType')
          ) {
            reject(
              new Error(
                'Invalid JSON format in glTF file. Accessor is missing required property.'
              )
            );
            return;
          }
          switch (accessor.type) {
            case 'SCALAR':
              accessorObject.itemSize = 1;
              break;
            case 'VEC2':
              accessorObject.itemSize = 2;
              break;
            case 'VEC3':
              accessorObject.itemSize = 3;
              break;
            case 'VEC4':
              accessorObject.itemSize = 4;
              break;
            case 'MAT2':
              accessorObject.itemSize = 4;
              break;
            case 'MAT3':
              accessorObject.itemSize = 9;
              break;
            case 'MAT4':
              accessorObject.itemSize = 16;
              break;
            default:
              accessorObject.itemSize = 1;
          }

          let bufferViewOffset =
						glTFBinaryDataByteOffset +
						parseInt(bufferView.byteOffset) +
						parseInt(accessor.byteOffset);

          // check if buffer is long enough
          if (
            binaryGeometry.length <
						bufferViewOffset + accessor.count * accessorObject.itemSize
          ) {
            reject(new Error('Error: buffer length exceeded'));
            return;
          }

          let tmpBuf, tmpView, curView;
          let size = accessor.count * accessorObject.itemSize;
          switch (accessor.componentType) {
            case 5120:
              accessorObject.typedArray = new Int8Array(
                binaryGeometry,
                bufferViewOffset,
                size
              );
              break;
            case 5121:
              accessorObject.typedArray = new Uint8Array(
                binaryGeometry,
                bufferViewOffset,
                size
              );
              break;
            case 5122:
              if (bufferViewOffset % 2 == 0)
                accessorObject.typedArray = new Int16Array(
                  binaryGeometry,
                  bufferViewOffset,
                  size
                );
              else {
                that.warn('Geometry loader encountered unaligned data.');
                tmpBuf = new ArrayBuffer(
                  2 * size
                );
                tmpView = new Int8Array(tmpBuf);
                curView = new Int8Array(
                  binaryGeometry,
                  bufferViewOffset,
                  2 * size
                );
                for (let i = 0, i_max = 2 * size; i < i_max; ++i) {
                  tmpView[i] = curView[i];
                }
                accessorObject.typedArray = new Int16Array(
                  tmpBuf,
                  0,
                  accessor.count * accessorObject.itemSize
                );
              }
              break;
            case 5123:
              if (bufferViewOffset % 2 == 0)
                accessorObject.typedArray = new Uint16Array(
                  binaryGeometry,
                  bufferViewOffset,
                  size
                );
              else {
                that.warn('Geometry loader encountered unaligned data.');
                tmpBuf = new ArrayBuffer(
                  2 * size
                );
                tmpView = new Int8Array(tmpBuf);
                curView = new Int8Array(
                  binaryGeometry,
                  bufferViewOffset,
                  2 * size
                );
                for (let i = 0, i_max = 2 * size; i < i_max; ++i) {
                  tmpView[i] = curView[i];
                }
                accessorObject.typedArray = new Uint16Array(
                  tmpBuf,
                  0,
                  size
                );
              }
              break;
            case 5124:
              that.warn(
                'Encountered 16 bit integer as type for array buffer. This is illegal for webGL. Ignoring it.'
              );
              break;
            case 5125:
              that.warn(
                'Encountered 16 bit unsigned integer as type for array buffer. This is illegal for webGL. Ignoring it.'
              );
              break;
            case 5126:
              if (bufferViewOffset % 4 == 0)
                accessorObject.typedArray = new Float32Array(
                  binaryGeometry,
                  bufferViewOffset,
                  size
                );
              else {
                that.warn('Geometry loader encountered unaligned data.');
                tmpBuf = new ArrayBuffer(
                  4 * size
                );
                tmpView = new Int8Array(tmpBuf);
                curView = new Int8Array(
                  binaryGeometry,
                  bufferViewOffset,
                  4 * size
                );
                for (let i = 0, i_max = 4 * size; i < i_max; ++i) {
                  tmpView[i] = curView[i];
                }
                accessorObject.typedArray = new Float32Array(
                  tmpBuf,
                  0,
                  size
                );
              }
              break;
            default:
              accessorObject.typedArray = new Float32Array(
                binaryGeometry,
                bufferViewOffset,
                size
              );
          }
          accessors[accessorNames[accessorNameIndex]] = accessorObject;
        }
      }

      // read material definitions
      let materialLibrary = {};
      if (glTFObject.hasOwnProperty('materials')) {
        let materials = glTFObject.materials;
        for (let mat in materials) {
          if (!materials[mat].hasOwnProperty('extensions')) {
            reject(
              new Error(
                'ShapeDiverGLBLoader got invalid data: material definition did not contain extensions member.'
              )
            );
            return;
          }
          if (!materials[mat].extensions.hasOwnProperty('KHR_materials_common')) {
            reject(
              new Error(
                'ShapeDiverGLBLoader got invalid data: material definition did not contain KHR_materials_common member.'
              )
            );
            return;
          }
          if (!materials[mat].extensions.KHR_materials_common.hasOwnProperty(
            'technique'
          )) {
            reject(
              new Error(
                'ShapeDiverGLBLoader got invalid data: material definition did not contain technique member.'
              )
            );
            return;
          }
          if (
            materials[mat].extensions.KHR_materials_common.technique != 'BLINN'
          ) {
            reject(
              new Error(
                'ShapeDiverGLBLoader got invalid data: only BLINN material technique is supported, we got ' +
								materials[mat].extensions.KHR_materials_common.technique
              )
            );
            return;
          }
          if (!materials[mat].extensions.KHR_materials_common.hasOwnProperty(
            'values'
          )) {
            reject(
              new Error(
                'ShapeDiverGLBLoader got invalid data: material definition did not contain values member.'
              )
            );
            return;
          }

          let newMaterial = new MATERIAL_PROPERTIES_STANDARD();
          let materialDefinition =
						materials[mat].extensions.KHR_materials_common.values;

          if (materialDefinition.hasOwnProperty('doubleSided')) {
            if (materialDefinition.doubleSided) {
              newMaterial.side = THREE.DoubleSide;
            }
            else {
              newMaterial.side = THREE.FrontSide;
            }
          }

          if (materialDefinition.hasOwnProperty('diffuse')) {
            let c = materialDefinition.diffuse.slice(0,3);
            for (let i = 0; i < 3; ++i) c[i] *= 255;
            newMaterial.color = TO_TINY_COLOR(c).toThreeColor();
            let t = Math.max(0.0,Math.min(materialDefinition.diffuse[3],1.0));
            newMaterial.opacity = t;
            newMaterial.transparent = t < 1;
          }
          if (materialDefinition.hasOwnProperty('shininess')) {
            newMaterial.metalness = Math.min(1, materialDefinition.shininess);
            newMaterial.roughness = 1 - Math.min(1, materialDefinition.shininess);
          }
          if (materialDefinition.hasOwnProperty('transparency')) {
            let t = Math.max(0.0,Math.min(materialDefinition.transparency,1.0));
            newMaterial.opacity = t;
            newMaterial.transparent = t < 1;
          }
          if (materialDefinition.hasOwnProperty('transparent')) {
            let transpBool = GLOBAL_UTILS.toBoolean(materialDefinition.transparent);
            newMaterial.transparent = transpBool;
            if (!transpBool) {
              newMaterial.opacity = 1.0;
            }
          }
          if (materialDefinition.hasOwnProperty('roughness')) {
            newMaterial.roughness = Math.min(1, Math.max(0, materialDefinition.roughness));
          }
          if (materialDefinition.hasOwnProperty('metalness')) {
            newMaterial.metalness = Math.min(1, Math.max(0, materialDefinition.metalness));
          }
          materialLibrary[mat] = newMaterial;
        }
      }

      // read all mesh definitions from the data

      let meshLibrary = {};
      for (let meshName in glTFObject.meshes) {
        if (glTFObject.meshes.hasOwnProperty(meshName)) {
          let meshList = [];
          let meshDefinition = glTFObject.meshes[meshName];

          // check if we have al we need
          if (!meshDefinition.hasOwnProperty('primitives')) {
            reject(
              new Error(
                'ShapeDiverGLBLoader got invalid data: primitives property missing.'
              )
            );
            return;
          }

          for (let primitive of meshDefinition.primitives) {
            if (!primitive.hasOwnProperty('indices') ||
							!primitive.hasOwnProperty('attributes')
            ) {
              reject(
                new Error(
                  'ShapeDiverGLBLoader got invalid data: mesh definition primitive misses indices or attributes.'
                )
              );
              return;
            }
            if (!primitive.attributes.hasOwnProperty(
              'POSITION'
            )) {
              reject(
                new Error(
                  'ShapeDiverGLBLoader got invalid data: mesh definition primitive is missing POSITION'
                )
              );
              return;
            }

            // create geometry object
            let geometry = new THREE.BufferGeometry();

            /// indices
            if (!accessors.hasOwnProperty(primitive.indices)) {
              reject(
                new Error(
                  'ShapeDiverGLBLoader got invalid data: accessor for mesh primitives not found.'
                )
              );
              return;
            }

            let indexAccessor = accessors[primitive.indices];
            geometry.setIndex(
              new THREE.BufferAttribute(
                indexAccessor.typedArray,
                indexAccessor.itemSize
              )
            );

            /// positions
            if (!accessors.hasOwnProperty(
              primitive.attributes.POSITION
            )) {
              //TODO: error handling
              reject(
                new Error(
                  'ShapeDiverGLBLoader got invalid data: accessor for mesh positions not found'
                )
              );
              return;
            }
            let positionAccessor =
							accessors[primitive.attributes.POSITION];
            geometry.addAttribute(
              'position',
              new THREE.BufferAttribute(
                positionAccessor.typedArray,
                positionAccessor.itemSize
              )
            );

            /// normals
            let needFlatShading = false;
            if (
              primitive.attributes.hasOwnProperty('NORMAL')
            ) {
              if (
                accessors.hasOwnProperty(
                  primitive.attributes.NORMAL
                )
              ) {
                let normalAccessor =
									accessors[primitive.attributes.NORMAL];
                let blnNormalsOk = false;
                for (let index = 0; index < 10; ++index) {
                  if (Math.abs(normalAccessor.typedArray[index * 3]) > 0.001) {
                    blnNormalsOk = true;
                    break;
                  }
                  if (
                    Math.abs(normalAccessor.typedArray[index * 3 + 1]) > 0.001
                  ) {
                    blnNormalsOk = true;
                    break;
                  }
                  if (
                    Math.abs(normalAccessor.typedArray[index * 3 + 2]) > 0.001
                  ) {
                    blnNormalsOk = true;
                    break;
                  }
                }

                // check if we have valid normals
                if (!blnNormalsOk) {
                  geometry.computeVertexNormals();
                } else {
                  geometry.addAttribute(
                    'normal',
                    new THREE.BufferAttribute(
                      normalAccessor.typedArray,
                      normalAccessor.itemSize
                    )
                  );
                }
              }
            } else {
              // if there are no normals, we require flat shading
              needFlatShading= true;
            }

            /// vertex colors
            let hasVertexColors = false;
            if (primitive.attributes.hasOwnProperty('COLOR')) {
              if (accessors.hasOwnProperty(primitive.attributes.COLOR)) {
                let vertexColorAccessor =
                  accessors[primitive.attributes.COLOR];
                geometry.addAttribute(
                  'color',
                  new THREE.BufferAttribute(
                    vertexColorAccessor.typedArray,
                    vertexColorAccessor.itemSize,
                    true)
                );
                hasVertexColors = true;
              }
            }

            /// texture coordinates
            let hasTexCoord = false;
            if (
              primitive.attributes.hasOwnProperty(
                'TEXCOORD_0'
              )
            ) {
              if (
                accessors.hasOwnProperty(
                  primitive.attributes.TEXCOORD_0
                )
              ) {
                let texCoordAccessor =
									accessors[primitive.attributes.TEXCOORD_0];
                geometry.addAttribute(
                  'uv',
                  new THREE.BufferAttribute(
                    texCoordAccessor.typedArray,
                    texCoordAccessor.itemSize
                  )
                );
                hasTexCoord = true;
              }
            }

            // material
            let material = null;
            if (primitive.hasOwnProperty('material')) {
              let meshMaterial = primitive.material;
              if (materialLibrary.hasOwnProperty(meshMaterial)) {
                material = materialLibrary[meshMaterial];
              }
            }

            let meshDescriptor = {
              geometry: geometry,
              hasTexCoord: hasTexCoord,
              hasVertexColors: hasVertexColors,
              flatShading: needFlatShading
            };
            if (material != null) {
              meshDescriptor.material = material;
            }
            meshList.push(meshDescriptor);
          }

          meshLibrary[meshName] = meshList;
        }
      }

      // we assume that meshes belonging to one object are bundled in a node
      // we completely ignore the scene attribute

      let outputGeometry = [];

      let nodeNames = Object.getOwnPropertyNames(glTFObject.nodes);
      if (!Array.isArray(transforms) ||
          transforms.length == 0 ||
          !transforms.every(function(m) {return m && m.isMatrix4;})
      ) {
        transforms = [new THREE.Matrix4()];
      }

      for (let nodeNameIndex in nodeNames) {
        let node = glTFObject.nodes[nodeNames[nodeNameIndex]];
        let nodeName = nodeNames[nodeNameIndex];

        let matrixGLTF = new THREE.Matrix4();
        if (node.hasOwnProperty('matrix')) {
          // node.matrix is given in column-major format, THREE.Matrix4.fromArray needs column-major, we are good
          matrixGLTF.fromArray(node.matrix);
        }

        if (node.hasOwnProperty('meshes')) {
          for (
            let transformIndex = 0; transformIndex < transforms.length;
            ++transformIndex
          ) {
            // create intermediate Object3D holding information about this node and transformation
            let matrixExternal = transforms[transformIndex];
            let matrixFinal = new THREE.Matrix4();
            matrixFinal.multiplyMatrices(matrixExternal, matrixGLTF);

            for (let meshNameIndex in node.meshes) {
              let meshName = node.meshes[meshNameIndex];
              if (meshLibrary.hasOwnProperty(meshName)) {
                for (let j = 0, j_max = meshLibrary[meshName].length; j < j_max; ++j) {

                  let pathString = 'transformation_' + transformIndex + '.' + nodeName + '.' + meshName + '.primitive_' + j;
                  let geometryDefinition = {
                    path: pathString,
                    geometry: meshLibrary[meshName][j].geometry,
                    texCoord: meshLibrary[meshName][j].hasTexCoord,
                    vertexColors: meshLibrary[meshName][j].hasVertexColors,
                    initialMatrix: matrixFinal,
                    type: 'mesh'
                  };
                  if (meshLibrary[meshName][j].hasOwnProperty('material')) {
                    if (meshLibrary[meshName][j].flatShading) {
                      // otherwise the flatShading was already set to true via the material (higher priority)
                      if(meshLibrary[meshName][j].material.flatShading === false)
                        meshLibrary[meshName][j].material.flatShading = meshLibrary[meshName][j].flatShading;
                    }
                    geometryDefinition.material = meshLibrary[meshName][j].material;
                  }
                  else {
                    geometryDefinition.flatShade = meshLibrary[meshName][j].flatShading;
                  }
                  outputGeometry.push(geometryDefinition);
                }
              }
            }
          }
        }
      }

      // now we read the rest of the geometry (special stuff which ShapeDiver has added to the spec)
      let sdgTFmagic = '';
      for (let i = 0; i < 5; ++i) {
        sdgTFmagic =
					sdgTFmagic +
					String.fromCharCode(dataView.getUint8(binaryglTFTotalLength + i));
      }

      if (sdgTFmagic != 'sdgTF') {
        that.warn(
          'ShapeDiverGLBLoader got invalid data, sdgTF magic wrong : ' +
					sdgTFmagic
        );
        resolve(outputGeometry);
        return;
      }

      /* not used at the moment
      let sdgTFversion = dataView.getUint32(binaryglTFTotalLength + 5, true);
      let binarysdgTFTotalLength = dataView.getUint32(
        binaryglTFTotalLength + 9,
        true
      );
			*/

      let sdgTFDescByteLength = dataView.getUint32(
        binaryglTFTotalLength + 13,
        true
      );

      /* not used at the moment
      let sdgTFDescFormat = dataView.getUint32(
        binaryglTFTotalLength + 17,
        true
      );
			*/

      let sdgTFString = '';
      let intStringKBLength = Math.ceil(sdgTFDescByteLength / 1024);
      for (let i = 0; i < intStringKBLength; ++i) {
        let readLength = 1024;
        if (sdgTFDescByteLength - i * 1024 < 1024) {
          readLength = sdgTFDescByteLength - i * 1024;
        }
        sdgTFString = sdgTFString + String.fromCharCode.apply(null, new Uint8Array(binaryGeometry, binaryglTFTotalLength + 21 + i * 1024, readLength));
      }
      let sdgTFObject = JSON.parse(sdgTFString);

      let sdgTFBinaryDataByteOffset =
				binaryglTFTotalLength + 21 + sdgTFDescByteLength;

      // check if all the important properties are there

      if (!sdgTFObject.hasOwnProperty('accessors')) {
        that.warn(
          'ShapeDiverGLBLoader got invalid data for ShapeDiver formats. Returning pure glTF-based object only.'
        );
        resolve(outputGeometry);
        return;
      }

      // get accessors

      accessors = {};
      accessorNames = Object.getOwnPropertyNames(sdgTFObject.accessors);

      for (let sdgTFaccessorNameIndex in accessorNames) {
        let sdgTFaccessor =
					sdgTFObject.accessors[accessorNames[sdgTFaccessorNameIndex]];

        // create accessor object
        let sdgTFaccessorObject = {};

        // check if we read a vector or a scalar
        if (!sdgTFaccessor.hasOwnProperty('type') ||
					!sdgTFaccessor.hasOwnProperty('count') ||
					!sdgTFaccessor.hasOwnProperty('componentType')
        ) {
          that.warn(
            'ShapeDiverGLBLoader got invalid data for ShapeDiver formats. Returning pure glTF-based object only.'
          );
          resolve(outputGeometry);
          return;
        }
        switch (sdgTFaccessor.type) {
          case 'SCALAR':
            sdgTFaccessorObject.itemSize = 1;
            break;
          case 'VEC2':
            sdgTFaccessorObject.itemSize = 2;
            break;
          case 'VEC3':
            sdgTFaccessorObject.itemSize = 3;
            break;
          case 'VEC4':
            sdgTFaccessorObject.itemSize = 4;
            break;
          case 'VEC7':
            sdgTFaccessorObject.itemSize = 7;
            break;
          case 'VEC10':
            sdgTFaccessorObject.itemSize = 10;
            break;
          case 'VEC12':
            sdgTFaccessorObject.itemSize = 12;
            break;
          case 'MAT2':
            sdgTFaccessorObject.itemSize = 4;
            break;
          case 'MAT3':
            sdgTFaccessorObject.itemSize = 9;
            break;
          case 'MAT4':
            sdgTFaccessorObject.itemSize = 16;
            break;
          default:
            sdgTFaccessorObject.itemSize = 1;
        }

        let sdgTFAccessorOffset =
					sdgTFBinaryDataByteOffset + parseInt(sdgTFaccessor.byteOffset);

        // check if buffer is long enough
        if (binaryGeometry.length < sdgTFAccessorOffset + sdgTFaccessor.count * sdgTFaccessor.itemSize) {
          that.warn('Error: buffer length exceeded');
          //TODO: error handling
        }

        switch (sdgTFaccessor.componentType) {
          case 5120:
            sdgTFaccessorObject.typedArray = new Int8Array(
              binaryGeometry,
              sdgTFAccessorOffset,
              sdgTFaccessor.count * sdgTFaccessorObject.itemSize
            );
            break;
          case 5121:
            sdgTFaccessorObject.typedArray = new Uint8Array(
              binaryGeometry,
              sdgTFAccessorOffset,
              sdgTFaccessor.count * sdgTFaccessorObject.itemSize
            );
            break;
          case 5122:
            sdgTFaccessorObject.typedArray = new Int16Array(
              binaryGeometry,
              sdgTFAccessorOffset,
              sdgTFaccessor.count * sdgTFaccessorObject.itemSize
            );
            break;
          case 5123:
            sdgTFaccessorObject.typedArray = new Uint16Array(
              binaryGeometry,
              sdgTFAccessorOffset,
              sdgTFaccessor.count * sdgTFaccessorObject.itemSize
            );
            break;
          case 5126:
            sdgTFaccessorObject.typedArray = new Float32Array(
              binaryGeometry,
              sdgTFAccessorOffset,
              sdgTFaccessor.count * sdgTFaccessorObject.itemSize
            );
            break;
          default:
            sdgTFaccessorObject.typedArray = new Float32Array(
              binaryGeometry,
              sdgTFAccessorOffset,
              sdgTFaccessor.count * sdgTFaccessorObject.itemSize
            );
        }

        sdgTFaccessorObject.count = sdgTFaccessor.count;

        accessors[accessorNames[sdgTFaccessorNameIndex]] = sdgTFaccessorObject;
      }

      // spheres

      if (sdgTFObject.hasOwnProperty('spheres')) {
        let sphereLibrary = sdgTFObject.spheres;
        if (sphereLibrary.hasOwnProperty('attributes')) {
          let sphereAttributes = sphereLibrary.attributes;
          if (sphereAttributes.hasOwnProperty('SPHERES')) {
            let sphereAccessor = accessors[sphereAttributes.SPHERES];

            let threeSphere;
            for (let j = 0, j_max = sphereAccessor.count; j < j_max; ++j) {
              threeSphere = new THREE.SphereBufferGeometry(
                sphereAccessor.typedArray[j * 4 + 3],
                20,
                20
              );
              threeSphere.translate(
                sphereAccessor.typedArray[j * 4],
                sphereAccessor.typedArray[j * 4 + 1],
                sphereAccessor.typedArray[j * 4 + 2]
              );

              let pathString = 'sdgtf_sphere_' + (j + 1).toString();
              let geometryDefinition = {
                path: pathString,
                geometry: threeSphere,
                texCoord: false,
                type: 'mesh'
              };
              outputGeometry.push(geometryDefinition);
            }
          }
        }
      }

      // circles

      if (sdgTFObject.hasOwnProperty('circles')) {
        let circleLibrary = sdgTFObject.circles;
        if (circleLibrary.hasOwnProperty('attributes')) {
          let circleAttributes = circleLibrary.attributes;
          if (circleAttributes.hasOwnProperty('CIRCLES')) {
            let circleAccessor = accessors[circleAttributes.CIRCLES];

            for (let j = 0, j_max = circleAccessor.count; j < j_max; ++j) {
              let circleCenter = new THREE.Vector3(
                circleAccessor.typedArray[j * 10],
                circleAccessor.typedArray[j * 10 + 1],
                circleAccessor.typedArray[j * 10 + 2]
              );
              let circleXAxis = new THREE.Vector3(
                circleAccessor.typedArray[j * 10 + 3],
                circleAccessor.typedArray[j * 10 + 4],
                circleAccessor.typedArray[j * 10 + 5]
              );
              let circleYAxis = new THREE.Vector3(
                circleAccessor.typedArray[j * 10 + 6],
                circleAccessor.typedArray[j * 10 + 7],
                circleAccessor.typedArray[j * 10 + 8]
              );
              let circleRadius = circleAccessor.typedArray[j * 10 + 9];
              let circleZAxis = new THREE.Vector3();
              circleZAxis.crossVectors(circleXAxis, circleYAxis);

              if (circleRadius > 0) {
                let curve = new THREE.ArcCurve(
                  0,
                  0, // ax, aY
                  circleRadius, // xRadius
                  0,
                  2.0 * Math.PI, // aStartAngle, aEndAngle
                  false // aClockwis
                );

                let circleGeometry = new THREE.Geometry().setFromPoints( curve.getPoints(50) );

                // rotate
                let rotMatrix = new THREE.Matrix4();
                rotMatrix.set(
                  circleXAxis.x,
                  circleYAxis.x,
                  circleZAxis.x,
                  0,
                  circleXAxis.y,
                  circleYAxis.y,
                  circleZAxis.y,
                  0,
                  circleXAxis.z,
                  circleYAxis.z,
                  circleZAxis.z,
                  0,
                  0,
                  0,
                  0,
                  1
                );
                circleGeometry.applyMatrix(rotMatrix);

                // translate
                circleGeometry.translate(
                  circleCenter.x,
                  circleCenter.y,
                  circleCenter.z
                );

                circleGeometry.sdgtfType = 'circle';

                let pathString = 'sdgtf_circle_' + (j + 1).toString();
                let geometryDefinition = {
                  path: pathString,
                  geometry: circleGeometry,
                  type: 'line'
                };
                outputGeometry.push(geometryDefinition);
              }
            }
          }
        }
      }

      // cylinders

      if (sdgTFObject.hasOwnProperty('cylinders')) {
        let cylinderLibrary = sdgTFObject.cylinders;
        if (cylinderLibrary.hasOwnProperty('attributes')) {
          let cylinderAttributes = cylinderLibrary.attributes;
          if (cylinderAttributes.hasOwnProperty('CYLINDERS')) {
            let cylinderAccessor = accessors[cylinderAttributes.CYLINDERS];

            let threeCylinder;
            for (let j = 0, j_max = cylinderAccessor.count; j < j_max; ++j) {
              let cylinderTop = new THREE.Vector3(
                cylinderAccessor.typedArray[j * 7],
                cylinderAccessor.typedArray[j * 7 + 1],
                cylinderAccessor.typedArray[j * 7 + 2]
              );
              let cylinderBottom = new THREE.Vector3(
                cylinderAccessor.typedArray[j * 7 + 3],
                cylinderAccessor.typedArray[j * 7 + 4],
                cylinderAccessor.typedArray[j * 7 + 5]
              );
              let cylinderRadius = cylinderAccessor.typedArray[j * 7 + 6];
              let cylinderAxis = new THREE.Vector3();
              cylinderAxis.subVectors(cylinderTop, cylinderBottom);

              let dotX = Math.abs(cylinderAxis.dot(new THREE.Vector3(1, 0, 0)));
              let dotY = Math.abs(cylinderAxis.dot(new THREE.Vector3(0, 1, 0)));

              let cylinderXAxis = new THREE.Vector3();
              if (dotX < dotY)
                cylinderXAxis.crossVectors(
                  cylinderAxis,
                  new THREE.Vector3(1, 0, 0)
                );
              else
                cylinderXAxis.crossVectors(
                  cylinderAxis,
                  new THREE.Vector3(0, 1, 0)
                );

              let cylinderYAxis = new THREE.Vector3();
              cylinderYAxis.crossVectors(cylinderAxis, cylinderXAxis);

              cylinderAxis.normalize();
              cylinderXAxis.normalize();
              cylinderYAxis.normalize();

              threeCylinder = new THREE.CylinderBufferGeometry(
                cylinderRadius,
                cylinderRadius,
                cylinderTop.distanceTo(cylinderBottom),
                50,
                1,
                true
              );

              // correct orientation
              threeCylinder.rotateX(0.5 * Math.PI);
              threeCylinder.translate(
                0,
                0,
                0.5 * cylinderTop.distanceTo(cylinderBottom)
              );

              // rotate
              let cylRotMatrix = new THREE.Matrix4();
              cylRotMatrix.set(
                cylinderXAxis.x,
                cylinderYAxis.x,
                cylinderAxis.x,
                0,
                cylinderXAxis.y,
                cylinderYAxis.y,
                cylinderAxis.y,
                0,
                cylinderXAxis.z,
                cylinderYAxis.z,
                cylinderAxis.z,
                0,
                0,
                0,
                0,
                1
              );
              threeCylinder.applyMatrix(cylRotMatrix);

              // translate
              threeCylinder.translate(
                cylinderBottom.x,
                cylinderBottom.y,
                cylinderBottom.z
              );

              let pathString = 'sdgtf_cylinder_' + (j + 1).toString();
              let geometryDefinition = {
                path: pathString,
                geometry: threeCylinder,
                texCoord: false,
                type: 'mesh'
              };
              outputGeometry.push(geometryDefinition);
            }
          }
        }
      }

      // arcs

      if (sdgTFObject.hasOwnProperty('arcs')) {
        let arcLibrary = sdgTFObject.arcs;
        if (arcLibrary.hasOwnProperty('attributes')) {
          let arcAttributes = arcLibrary.attributes;
          if (arcAttributes.hasOwnProperty('ARCS')) {
            let arcAccessor = accessors[arcAttributes.ARCS];

            for (let j = 0, j_max = arcAccessor.count; j < j_max; ++j) {
              let arcCenter = new THREE.Vector3(
                arcAccessor.typedArray[j * 12],
                arcAccessor.typedArray[j * 12 + 1],
                arcAccessor.typedArray[j * 12 + 2]
              );
              let arcXAxis = new THREE.Vector3(
                arcAccessor.typedArray[j * 12 + 3],
                arcAccessor.typedArray[j * 12 + 4],
                arcAccessor.typedArray[j * 12 + 5]
              );
              let arcYAxis = new THREE.Vector3(
                arcAccessor.typedArray[j * 12 + 6],
                arcAccessor.typedArray[j * 12 + 7],
                arcAccessor.typedArray[j * 12 + 8]
              );
              let arcRadius = arcAccessor.typedArray[j * 12 + 9];
              let arcMinAngle = arcAccessor.typedArray[j * 12 + 10];
              let arcMaxAngle = arcAccessor.typedArray[j * 12 + 11];
              let arcZAxis = new THREE.Vector3();
              arcZAxis.crossVectors(arcXAxis, arcYAxis);

              if (arcRadius > 0) {
                let arcCurve = new THREE.ArcCurve(
                  0,
                  0, // ax, aY
                  arcRadius, // xRadius
                  arcMinAngle,
                  arcMaxAngle, // aStartAngle, aEndAngle
                  false // aClockwis // aRotation
                );

                let arcGeometry = new THREE.Geometry().setFromPoints( arcCurve.getPoints(
                  Math.max(
                    3,
                    Math.round(
                      50 * ((arcMaxAngle - arcMinAngle) / 2 * Math.PI)
                    )
                  )
                ) );

                // rotate
                let arcRotMatrix = new THREE.Matrix4();
                arcRotMatrix.set(
                  arcXAxis.x,
                  arcYAxis.x,
                  arcZAxis.x,
                  0,
                  arcXAxis.y,
                  arcYAxis.y,
                  arcZAxis.y,
                  0,
                  arcXAxis.z,
                  arcYAxis.z,
                  arcZAxis.z,
                  0,
                  0,
                  0,
                  0,
                  1
                );
                arcGeometry.applyMatrix(arcRotMatrix);

                // translate
                arcGeometry.translate(arcCenter.x, arcCenter.y, arcCenter.z);
                arcGeometry.sdgtfType = 'arc';

                let pathString = 'sdgtf_arc_' + (j + 1).toString();
                let geometryDefinition = {
                  path: pathString,
                  geometry: arcGeometry,
                  type: 'line'
                };
                outputGeometry.push(geometryDefinition);
              }
            }
          }
        }
      }

      // beziercurves
      // optionally ignore beziercurves, because verb can not be used with react-native (expo)
      if (verb) {
        if (sdgTFObject.hasOwnProperty('beziercurves')) {
          let curveLibrary = sdgTFObject.beziercurves;

          let curveIndex = 0;
          for (let curveName in curveLibrary) {
            let bCurve = curveLibrary[curveName];

            if (
              bCurve.hasOwnProperty('attributes') &&
              bCurve.hasOwnProperty('degree')
            ) {
              let curveAttributes = bCurve.attributes;
              if (
                curveAttributes.hasOwnProperty('KNOTS') &&
                curveAttributes.hasOwnProperty('CONTROLPOINTS')
              ) {
                let curveKnotAccessor = accessors[curveAttributes.KNOTS];
                let verbKnots = [];
                for (let i = 0, i_max = curveKnotAccessor.typedArray.length; i < i_max; ++i) {
                  verbKnots.push(curveKnotAccessor.typedArray[i]);
                  // verb js needs superfluous first and last knot
                  if (i == 0 || i == curveKnotAccessor.typedArray.length - 1)
                    verbKnots.push(curveKnotAccessor.typedArray[i]);
                }

                let curveCPAccessor = accessors[curveAttributes.CONTROLPOINTS];
                let verbCP = [];
                for (let i = 0, i_max = curveCPAccessor.typedArray.length / 3; i < i_max; ++i) {
                  let cp = [
                    curveCPAccessor.typedArray[i * 3],
                    curveCPAccessor.typedArray[i * 3 + 1],
                    curveCPAccessor.typedArray[i * 3 + 2]
                  ];
                  verbCP.push(cp);
                }

                let curveDegree = bCurve.degree;

                let verbNURBSCurve;
                try {
                  verbNURBSCurve = verb.geom.NurbsCurve.byKnotsControlPointsWeights(
                    curveDegree,
                    verbKnots,
                    verbCP
                  );
                } catch (err) {
                  that.warn(
                    'verb nurbs curve creation failed with error ' + err
                  );
                  continue;
                }

                let threeCurveGeometry = verbNURBSCurve.toThreeGeometry(tol);
                threeCurveGeometry.sdgtfType = 'beziercurve';

                let pathString = 'sdgtf_curve_' + (curveIndex + 1).toString();
                let geometryDefinition = {
                  path: pathString,
                  geometry: threeCurveGeometry,
                  type: 'line'
                };
                outputGeometry.push(geometryDefinition);
                ++curveIndex;
              }
            }
          }
        }
      }

      // nurbs surfaces
      // optionally ignore surfacepatches, because verb can not be used with react-native (expo)
      if (verb) {
        if (sdgTFObject.hasOwnProperty('surfacepatches')) {
          let surfaceLibrary = sdgTFObject.surfacepatches;

          let surfaceIndex = 0;
          for (let surfaceName in surfaceLibrary) {
            let sPatch = surfaceLibrary[surfaceName];

            if (
              sPatch.hasOwnProperty('attributes') &&
              sPatch.hasOwnProperty('degreeU') &&
              sPatch.hasOwnProperty('degreeV') &&
              sPatch.hasOwnProperty('controlPointCountU') &&
              sPatch.hasOwnProperty('controlPointCountV')
            ) {
              let surfaceAttributes = sPatch.attributes;
              if (
                surfaceAttributes.hasOwnProperty('KNOTSU') &&
                surfaceAttributes.hasOwnProperty('KNOTSV') &&
                surfaceAttributes.hasOwnProperty('CONTROLPOINTS')
              ) {
                let surfaceKnotUAccessor = accessors[surfaceAttributes.KNOTSU];
                let verbKnotsU = [];
                for (let i = 0, i_max = surfaceKnotUAccessor.typedArray.length; i < i_max; ++i) {
                  verbKnotsU.push(surfaceKnotUAccessor.typedArray[i]);
                  // verb js needs superfluous first and last knot
                  if (i == 0 || i == surfaceKnotUAccessor.typedArray.length - 1)
                    verbKnotsU.push(surfaceKnotUAccessor.typedArray[i]);
                }

                let surfaceKnotVAccessor = accessors[surfaceAttributes.KNOTSV];
                let verbKnotsV = [];
                for (let i = 0, i_max = surfaceKnotVAccessor.typedArray.length; i < i_max; ++i) {
                  verbKnotsV.push(surfaceKnotVAccessor.typedArray[i]);
                  // verb js needs superfluous first and last knot
                  if (i == 0 || i == surfaceKnotVAccessor.typedArray.length - 1)
                    verbKnotsV.push(surfaceKnotVAccessor.typedArray[i]);
                }

                let cpCountU = sPatch.controlPointCountU;
                let cpCountV = sPatch.controlPointCountV;

                let surfaceCPAccessor = accessors[surfaceAttributes.CONTROLPOINTS];
                let verbSrfCP = [];
                let pointCount = 0;
                for (let i = 0; i < cpCountU; ++i) {
                  let currCPArr = [];
                  for (let j = 0; j < cpCountV; ++j) {
                    let sCp = [
                      surfaceCPAccessor.typedArray[pointCount * 3],
                      surfaceCPAccessor.typedArray[pointCount * 3 + 1],
                      surfaceCPAccessor.typedArray[pointCount * 3 + 2]
                    ];
                    currCPArr.push(sCp);
                    ++pointCount;
                  }
                  verbSrfCP.push(currCPArr);
                }

                let surfaceDegreeU = sPatch.degreeU;
                let surfaceDegreeV = sPatch.degreeV;

                let verbNURBSSurface;
                try {
                  verbNURBSSurface = verb.geom.NurbsSurface.byKnotsControlPointsWeights(
                    surfaceDegreeU,
                    surfaceDegreeV,
                    verbKnotsU,
                    verbKnotsV,
                    verbSrfCP
                  );
                } catch (err) {
                  that.warn(
                    'verb surface curve creation failed with error ' + err.message
                  );
                  continue;
                }

                let threeSurface = verbNURBSSurface.toThreeGeometry(tol);

                // FIXME: these should not be computed all the time, they might be expensive
                threeSurface.computeFaceNormals();
                threeSurface.computeVertexNormals();

                let pathString = 'sdgtf_surface_' + (surfaceIndex + 1).toString();
                let geometryDefinition = {
                  path: pathString,
                  geometry: threeSurface,
                  texCoord: false,
                  type: 'mesh'
                };
                outputGeometry.push(geometryDefinition);
                ++surfaceIndex;
              }
            }
          }
        }
      }

      // polylines
      if (sdgTFObject.hasOwnProperty('polylines')) {
        let polylineLibrary = sdgTFObject.polylines;

        let polylineIndex = 0;
        for (let polylineName in polylineLibrary) {
          let bPolyline = polylineLibrary[polylineName];
          if (bPolyline.hasOwnProperty('attributes')) {
            let polylineAttributes = bPolyline.attributes;
            if (polylineAttributes.hasOwnProperty('VERTICES')) {
              let polylineVertexAccessor =
								accessors[polylineAttributes.VERTICES];

              let polylineGeometry = new THREE.BufferGeometry();
              polylineGeometry.addAttribute( 'position', new THREE.BufferAttribute( polylineVertexAccessor.typedArray, 3 ) );
              polylineGeometry.sdgtfType = 'polyline';

              let pathString = 'sdgtf_polyline_' + (polylineIndex + 1).toString();
              let geometryDefinition = {
                path: pathString,
                geometry: polylineGeometry,
                type: 'line'
              };
              outputGeometry.push(geometryDefinition);
              ++polylineIndex;
            }
          }
        }
      }

      // points
      if (sdgTFObject.hasOwnProperty('points')) {
        let pointClouds = sdgTFObject.points;

        let pointCloudIndex = 0;
        for (let pointCloudName in pointClouds) {
          let bPointCloud = pointClouds[pointCloudName];
          if (bPointCloud.hasOwnProperty('attributes')) {
            let pointAttributes = bPointCloud.attributes;
            if (pointAttributes.hasOwnProperty('POINTS')) {
              let pointCloudAccessor = accessors[pointAttributes.POINTS];

              let pointGeometry = new THREE.Geometry();
              for (let i = 0, i_max = pointCloudAccessor.typedArray.length / 3; i < i_max; ++i) {
                let pVec = new THREE.Vector3(
                  pointCloudAccessor.typedArray[3 * i],
                  pointCloudAccessor.typedArray[3 * i + 1],
                  pointCloudAccessor.typedArray[3 * i + 2]
                );
                pointGeometry.vertices.push(pVec);
              }

              let pathString = 'sdgtf_pointcloud_' + (pointCloudIndex + 1).toString();
              let geometryDefinition = {
                path: pathString,
                geometry: pointGeometry,
                type: 'points'
              };
              outputGeometry.push(geometryDefinition);
              ++pointCloudIndex;
            }
          }
        }
      }
      resolve(outputGeometry);
    });
  };
}();

module.exports = GLBLoader;
